import React, { useState } from 'react';
import { SpecialEvent, DayOfWeek, HappyHourDeal } from '../types';

interface VenuePageProps {
  venue: SpecialEvent;
  onBack: () => void;
}

const WEEKDAYS = [
  { key: DayOfWeek.MONDAY, label: 'M' },
  { key: DayOfWeek.TUESDAY, label: 'T' },
  { key: DayOfWeek.WEDNESDAY, label: 'W' },
  { key: DayOfWeek.THURSDAY, label: 'T' },
  { key: DayOfWeek.FRIDAY, label: 'F' },
  { key: DayOfWeek.SATURDAY, label: 'S' },
  { key: DayOfWeek.SUNDAY, label: 'S' }
];

const formatTo12Hour = (time24: string) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const VenuePage: React.FC<VenuePageProps> = ({ venue, onBack }) => {
  const [copied, setCopied] = useState(false);

  const displayAddress = venue.streetAddress && venue.city && venue.state && venue.zipCode 
    ? `${venue.streetAddress}, ${venue.city}, ${venue.state} ${venue.zipCode}`
    : venue.address;

  const dealsByCategory = venue.deals.reduce((acc, deal) => {
    if (!acc[deal.category]) acc[deal.category] = [];
    acc[deal.category].push(deal);
    return acc;
  }, {} as Record<string, HappyHourDeal[]>);

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(displayAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isDemo = venue.listing_status === 'demo';
  const isVerified = Boolean(venue.isVerified ?? venue.is_verified);

  const getFullUrl = (handle: string | undefined, type: 'instagram' | 'facebook' | 'x') => {
    if (!handle) return '';
    if (handle.startsWith('http')) return handle;
    const cleanHandle = handle.replace('@', '');
    if (type === 'instagram') return `https://instagram.com/${cleanHandle}`;
    if (type === 'facebook') return `https://facebook.com/${cleanHandle}`;
    if (type === 'x') return `https://x.com/${cleanHandle}`;
    return handle;
  };

  const socials = [
    { 
      id: 'website', 
      url: venue.website ? (venue.website.startsWith('http') ? venue.website : `https://${venue.website}`) : '', 
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      )
    },
    { 
      id: 'instagram', 
      url: getFullUrl(venue.instagram, 'instagram'), 
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812-1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    { 
      id: 'facebook', 
      url: getFullUrl(venue.facebook, 'facebook'), 
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
        </svg>
      )
    },
    { 
      id: 'x', 
      url: getFullUrl(venue.xHandle, 'x'), 
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.045 4.126H5.078z" />
        </svg>
      )
    }
  ].filter(s => !!s.url);

  return (
    <div className="min-h-screen bg-[#FFFDF5] animate-in fade-in duration-700 pb-20">
      <section className="relative h-[40vh] md:h-[50vh] w-full bg-black overflow-hidden">
        <img 
          src={venue.imageUrl} 
          className="w-full h-full object-cover opacity-80" 
          alt={venue.venueName} 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFFDF5] via-transparent to-transparent" />
        <button 
          onClick={onBack}
          className="absolute top-24 md:top-32 left-6 md:left-12 z-50 w-12 h-12 rounded-full bg-black/50 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-amber-500 hover:text-black transition-all shadow-2xl"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </section>

      <div className="max-w-7xl mx-auto px-6 relative -mt-16 md:-mt-24 z-20">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div className="flex flex-col items-start gap-6 text-left w-full">
            {isVerified && venue.logoUrl && (
              <div className="w-24 h-24 md:w-36 md:h-36 rounded-[2.5rem] bg-white p-4 md:p-6 shadow-2xl border-4 border-white flex items-center justify-center overflow-hidden animate-float">
                <img src={venue.logoUrl} className="max-w-full max-h-full object-contain" alt="Logo" />
              </div>
            )}
            
            <div className="space-y-4 w-full">
              <div className="flex flex-wrap items-center gap-2">
                {isDemo && (
                  <span className="bg-gray-500/80 text-white font-black text-[9px] md:text-xs px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg border border-white/10">
                    Sample Listing
                  </span>
                )}
                <span className={`font-black text-[9px] md:text-xs px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg ${isVerified ? 'bg-amber-500 text-black' : 'bg-white text-black border border-black/10'}`}>
                  {isVerified ? 'Verified Partner' : 'Preview Listing'}
                </span>
              </div>
              
              <h1 className="text-5xl md:text-8xl font-bold serif tracking-tighter text-black leading-[0.9] drop-shadow-sm max-w-4xl">
                {venue.venueName}
              </h1>
              
              <div className="flex flex-col md:flex-row md:items-center gap-4 pt-2">
                <button 
                  onClick={handleCopyAddress}
                  className={`flex items-center gap-2.5 transition-all font-black uppercase tracking-widest text-[10px] md:text-xs px-5 py-3 rounded-xl border-2 self-start ${copied ? 'bg-green-500 border-green-500 text-white shadow-green-500/20' : 'bg-black text-white hover:bg-amber-500 hover:border-amber-500 hover:text-black shadow-lg shadow-black/10'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {copied ? 'COPIED ADDRESS' : displayAddress}
                </button>

                {socials.length > 0 && (
                  <div className="flex items-center gap-2 p-1.5 bg-black rounded-2xl w-fit shadow-lg shadow-black/10">
                    {socials.map((link) => (
                      <a 
                        key={link.id}
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center rounded-xl text-amber-500 hover:text-white hover:bg-white/10 hover:scale-105 transition-all"
                        title={link.id}
                      >
                        {link.icon}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 py-12 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-20">
          <div className="space-y-12">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-5xl font-black serif tracking-tight text-left leading-tight">
                {venue.specialType || venue.name || 'Happy Hour'} <span className="italic text-amber-600 text-4xl block md:inline md:ml-2">Specials.</span>
              </h2>
              
              <div className="flex flex-wrap items-center gap-6 animate-in fade-in slide-in-from-left-4 duration-500 bg-white p-4 md:p-6 rounded-[2rem] border border-black/[0.03] shadow-sm w-fit">
                <div className="flex flex-col gap-2">
                  <span className="text-[8px] font-black uppercase tracking-[0.3em] text-gray-400">Available Days</span>
                  <div className="flex gap-1.5">
                    {WEEKDAYS.map(day => {
                      const isActive = venue.days.includes(day.key as DayOfWeek);
                      return (
                        <div 
                          key={day.key} 
                          className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-black transition-all ${isActive ? 'bg-amber-500 text-black shadow-md shadow-amber-500/20' : 'bg-gray-100 text-gray-300'}`}
                        >
                          {day.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="w-px h-10 bg-black/5 hidden md:block" />
                
                <div className="flex flex-col gap-1.5">
                   <span className="text-[8px] font-black uppercase tracking-[0.3em] text-gray-400">Time Window</span>
                   <span className="bg-amber-400 text-black font-black text-[10px] md:text-xs px-4 py-1.5 rounded-full uppercase tracking-widest shadow-sm">
                    {formatTo12Hour(venue.startTime)} - {formatTo12Hour(venue.endTime)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-12">
              {(Object.entries(dealsByCategory) as [string, HappyHourDeal[]][]).map(([category, deals]) => (
                <div key={category} className="space-y-6">
                  <div className="flex items-center gap-4">
                    <h3 className="text-lg md:text-xl font-black uppercase tracking-[0.3em] text-amber-600">{category}</h3>
                    <div className="flex-1 h-px bg-black/[0.05]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                    {deals.map(deal => (
                      <div key={deal.id} className="flex justify-between items-baseline group py-3 border-b border-transparent hover:border-amber-500/10 transition-all">
                        <span className={`text-lg text-black group-hover:text-amber-600 transition-colors ${deal.isGroupDeal ? 'font-black italic' : 'font-bold'}`}>{deal.item}</span>
                        <div className="flex-1 border-b border-dotted border-black/[0.1] mx-4 mb-1" />
                        <span className="font-black text-xl text-amber-600">{deal.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {venue.notes && (
              <div className="p-6 bg-gray-50/50 rounded-2xl border border-black/[0.03] animate-in fade-in slide-in-from-top-2 duration-500">
                <p className="text-xs text-gray-500 font-medium italic leading-relaxed">
                  Note: {venue.notes}
                </p>
              </div>
            )}
          </div>

          {isVerified && venue.galleryImages && venue.galleryImages.length > 0 && (
            <div className="space-y-12 animate-in slide-in-from-bottom-6 duration-700">
              <h2 className="text-3xl md:text-5xl font-black serif tracking-tight text-left">Vibe <span className="italic text-amber-600 text-4xl">Check.</span></h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {venue.galleryImages.map((img, idx) => (
                  <div key={idx} className="aspect-square rounded-3xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all hover:scale-[1.02]">
                    <img src={img} className="w-full h-full object-cover" alt={`Gallery ${idx}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-32 h-fit text-left">
          <div className="bg-[#0a0a0a] text-white p-8 md:p-10 rounded-[3rem] shadow-2xl space-y-10 grainy">
             {isVerified && Array.isArray(venue.tags) && venue.tags.length > 0 && (
               <div className="space-y-6">
                  <h4 className="text-sm md:text-base font-black uppercase tracking-[0.4em] text-amber-500 border-b border-white/5 pb-3">Vibes</h4>
                  <div className="flex flex-wrap gap-2">
                    {venue.tags.map(tag => (
                      <span key={tag} className="text-[8px] font-black uppercase tracking-widest px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-amber-500 hover:text-black transition-colors">{tag}</span>
                    ))}
                  </div>
               </div>
             )}
             <div className="space-y-6">
                <h4 className="text-sm md:text-base font-black uppercase tracking-[0.4em] text-amber-500 border-b border-white/5 pb-3">Schedule</h4>
                <div className="space-y-3">
                  {WEEKDAYS.map(day => {
                    const isActive = venue.days.includes(day.key as DayOfWeek);
                    const label = day.key;
                    return (
                      <div key={day.key} className={`flex justify-between items-center ${isActive ? 'text-white' : 'text-white/10'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
                        <span className="text-[9px] font-bold tracking-wider">{isActive ? `${formatTo12Hour(venue.startTime)} - ${formatTo12Hour(venue.endTime)}` : 'N/A'}</span>
                      </div>
                    );
                  })}
                </div>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VenuePage;
